import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AbsoluteContainer,
  Button,
  Cushion,
  FlexContainer,
  Grid,
  ImageResizer,
  Rectangle,
  RelativeContainer,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { get, findIndex } from 'lodash';
import { useSwipeable } from 'react-swipeable';
import Icon from '../icon';
import ClubLink from '../club-link';
import clubUrl from '../../lib/routing/club-url';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import sanitizeParams from '../../lib/routing/sanitize-params';
import ClubRouter from '../../lib/routing';
import LoginPanel from '../auth/login-panel';
import PhotoComments from '../media/photo-comments';
import NativeStrip from '../native-adverts/strip';
import SeoHead from '../seo';
import ShareButton from '../social/share-button';

function Photo({ album, club, image, fixtureId, nextImage, teamId, title, prevImage }) {
  const ARROW_ICON_SIZE = 22;
  const [isOpen, setIsOpen] = useState(false);
  const shareUrl = clubUrl(club, 'media.image', { id: album.id, imageId: image.id });
  const backRoute = fixtureId ? 'team.match_centre.photos' : 'media.album';
  const backRouteParams = fixtureId ? { fixtureId, teamId } : { id: album.id, _title: album.title };
  const photoRoute = fixtureId ? 'team.match_centre.photos.image' : 'media.image';
  const photoRouteParams = fixtureId ? { albumId: album.id, fixtureId, teamId } : { id: album.id };

  const loadNextImage = () => {
    if (nextImage) {
      ClubRouter.replaceClubRoute(club, photoRoute, {
        ...photoRouteParams,
        imageId: nextImage.id,
      });
    }
  };

  const loadPrevImage = () => {
    if (prevImage) {
      ClubRouter.replaceClubRoute(club, photoRoute, {
        ...photoRouteParams,
        imageId: prevImage.id,
      });
    }
  };

  const getResizedImageUrl = (url) =>
    ImageResizer.resizeUrl(url, {
      o: 'jpg',
      q: '70',
      w: '1920',
    });

  const onKeyPressHandler = (e) => {
    const { key } = e;

    if (key === 'ArrowLeft') {
      loadPrevImage();
    }

    if (key === 'ArrowRight') {
      loadNextImage();
    }

    if (key === 'Escape') {
      ClubRouter.pushClubRoute(club, backRoute, sanitizeParams(backRouteParams));
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyPressHandler);

    return function cleanUp() {
      document.removeEventListener('keydown', onKeyPressHandler);
    };
  });

  const swipeHandlers = useSwipeable({
    onSwipedLeft: loadNextImage,
    onSwipedRight: loadPrevImage,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  return (
    <>
      <SeoHead
        club={club}
        descriptionTranslationKey="media:photo_meta_description"
        route="media.image"
        routeParams={{ id: album.id, imageId: image.id }}
        title={title}
        translationValues={{
          authorName: get(album, 'author.name'),
          albumName: get(album, 'title'),
        }}
      />
      <div {...swipeHandlers} style={{ position: 'fixed', height: '100%', width: '100%' }}>
        <Rectangle fill="#000" style={{ height: '100%' }}>
          <Grid
            columns="1fr"
            responsive={[{ minWidth: 'tab', props: { columns: isOpen ? '1fr 320px' : '1fr' } }]}
            rows="48px 1fr 48px auto"
            alignItems="center"
          >
            <Cushion
              horizontal="medium"
              responsive={[{ maxWidth: 'tab', props: { horizontal: 'small' } }]}
              style={{ gridColumn: 1, gridRow: 1 }}
            >
              <FlexContainer justifyContent="space-between" alignItems="center">
                {album && (
                  <Space horizontal="medium">
                    <ClubLink currentClub={club} route={backRoute} params={backRouteParams} replace>
                      <a>
                        <Icon name="back-arrow" color="mercury" size={24} />
                      </a>
                    </ClubLink>
                  </Space>
                )}
                <Typography
                  preset="subtitle--small"
                  color="grey"
                  align="center"
                  style={{ flex: 1, textIndent: club.commentsEnabled ? 24 : 0 }}
                >
                  {`${findIndex(album.images, { id: image.id }) + 1}/${album.images.length}`}
                </Typography>

                <FlexContainer>
                  <Space horizontal="medium">
                    <ShareButton
                      color="white"
                      url={shareUrl}
                      title={album.title}
                      iconSize={24}
                      description={title}
                      imageUrl={image.url}
                    />
                  </Space>
                  {club.commentsEnabled && (
                    <Space left="medium">
                      <Button
                        onClick={() => setIsOpen(!isOpen)}
                        theme="text"
                        className="comments-wrap"
                        style={{ position: 'relative' }}
                      >
                        <Icon name="comment" color="mercury" topOffset={2} size={24} />
                        {image.commentCount > 0 && (
                          <Rectangle fill="#000" radius="circle">
                            <Cushion horizontal="xsmall">
                              <Typography
                                color="white"
                                preset="body--small"
                                component="div"
                                style={{
                                  display: 'inline-block',
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  transform: 'translateX(50%)',
                                  wordBreak: 'keep-all',
                                }}
                              >
                                {image.commentCount}
                              </Typography>
                            </Cushion>
                          </Rectangle>
                        )}
                      </Button>
                    </Space>
                  )}
                </FlexContainer>
              </FlexContainer>
            </Cushion>

            <div
              style={{
                gridRow: 2,
                gridColumn: 1,
                textAlign: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <RelativeContainer>
                {prevImage && (
                  <AbsoluteContainer left={12} top="50%" style={{ zIndex: 1 }}>
                    <div
                      role="presentation"
                      onClick={loadPrevImage}
                      style={{ marginTop: -15, cursor: 'pointer' }}
                    >
                      <Cushion all={4}>
                        <Rectangle radius="circle" fill="rgba(0, 0, 0, 0.5 )">
                          <Icon name="arrow-left" color="mercury" size={ARROW_ICON_SIZE} />
                        </Rectangle>
                      </Cushion>
                    </div>
                  </AbsoluteContainer>
                )}
                {image && (
                  <div
                    key={image.id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url('${getResizedImageUrl(image.url)}')`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                    }}
                  >
                    <img
                      src={getResizedImageUrl(image.url)}
                      alt={image.title}
                      width={1}
                      height={1}
                      style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        border: 0,
                      }}
                    />
                  </div>
                )}
                {nextImage && (
                  <AbsoluteContainer right={12} top="50%" style={{ zIndex: 1 }}>
                    <div
                      role="presentation"
                      onClick={loadNextImage}
                      style={{ marginTop: -15, cursor: 'pointer' }}
                    >
                      <Cushion all={4}>
                        <Rectangle radius="circle" fill="rgba(0, 0, 0, 0.5 )">
                          <Icon name="arrow-right" color="mercury" size={ARROW_ICON_SIZE} />
                        </Rectangle>
                      </Cushion>
                    </div>
                  </AbsoluteContainer>
                )}
              </RelativeContainer>
            </div>

            <Typography
              color="grey"
              preset="subtitle--small"
              align="center"
              component="div"
              style={{ gridRow: 3, gridColumn: 1 }}
            >
              {title}
            </Typography>

            <NativeStrip
              fill="#000"
              paddingProps={{
                vertical: 'medium',
                horizontal: 'small',
                responsive: [{ maxWidth: 'tab', props: { horizontal: 'medium' } }],
              }}
            />

            {club.commentsEnabled && (
              <div style={{ gridRow: '1 / span 4', gridColumn: 2, height: '100vh' }}>
                <PhotoComments
                  club={club}
                  mediaType="image"
                  mediaId={image.id}
                  isOpen={isOpen}
                  closeComments={() => setIsOpen(false)}
                />
              </div>
            )}
          </Grid>
        </Rectangle>
        <LoginPanel />
      </div>
    </>
  );
}

Photo.defaultProps = {
  album: null,
  fixtureId: null,
  image: null,
  nextImage: null,
  prevImage: null,
  teamId: null,
};

Photo.propTypes = {
  album: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }),
  club: CLUB_PROP_TYPES.isRequired,
  fixtureId: PropTypes.string,
  image: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  nextImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  prevImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  teamId: PropTypes.number,
  title: PropTypes.string.isRequired,
};

export default Photo;
